import { useState } from "react";
import { eras, archiveYears } from "@/mocks/comics";

const dotColor: Record<string, string> = {
  primary: "bg-primary-500",
  accent: "bg-accent-500",
  secondary: "bg-secondary-500",
  foreground: "bg-foreground-700",
};

const chipColor: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800 border-primary-200",
  accent: "bg-accent-100 text-accent-800 border-accent-200",
  secondary: "bg-secondary-100 text-secondary-800 border-secondary-200",
  foreground: "bg-background-100 text-foreground-700 border-background-200",
};

export default function EraArchive() {
  const [active, setActive] = useState("era-80");
  const era = eras.find((e) => e.id === active) ?? eras[1];

  return (
    <section id="era" className="scroll-mt-20 bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-700">
            <span className="h-px w-6 bg-primary-500"></span>
            年代补档档案库
          </p>
          <h2 className="text-2xl md:text-4xl">老漫画按年代归档</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            从水墨之源的 70 年代到纸媒尾声的 00 年代，每一部童年经典都有它的坐标。
            点开年代，就能找到当年那部。
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <div
            className="flex gap-2 overflow-x-auto rounded-lg bg-background-50 p-2 lg:flex-col lg:overflow-visible"
            role="tablist"
            aria-label="年代选择"
          >
            {eras.map((e) => {
              const isActive = e.id === active;
              return (
                <button
                  key={e.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(e.id)}
                  className={`flex shrink-0 items-center gap-3 rounded-md px-4 py-3 text-left transition-colors lg:w-full ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
                  }`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      isActive ? "bg-background-50" : dotColor[e.accent]
                    }`}
                  ></span>
                  <span className="whitespace-nowrap">
                    <span className="block font-heading text-base">{e.label}</span>
                    <span
                      className={`text-xs ${isActive ? "text-background-50/80" : "text-foreground-500"}`}
                    >
                      {e.desc.length > 18 ? `${e.desc.slice(0, 14)}…` : e.desc}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-xl font-heading md:text-2xl">
                {era.label} <span className="text-sm text-foreground-500">· {era.year}</span>
              </h3>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                已收录 {era.count} 部补档作品
              </span>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {era.desc}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {era.tags.map((t) => (
                <span
                  key={t}
                  className={`rounded-full border px-3 py-1 text-xs whitespace-nowrap ${chipColor[era.accent]}`}
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-6 border-t border-dashed border-background-300 pt-5">
              <h4 className="mb-3 text-sm font-medium text-foreground-500">本年代补档索引</h4>
              <div className="flex flex-wrap gap-2">
                {archiveYears.map((y) => (
                  <span
                    key={y}
                    className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                  >
                    {y}
                  </span>
                ))}
              </div>
            </div>

            <a
              href="#classics"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
            >
              去经典补档里翻这一年代
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}